import React from 'react';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import type { AdminInvoiceReportColumn } from '@/features/admin_invoice_reports/types';
import {
  ActionCell,
  StatusCell,
  TooltipCell,
} from '@/features/admin_invoice_reports/components/List/Table/Cells';
import {
  Checkbox,
  IconDrawer,
  TableProps,
  Tag,
  TagProps,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import classnames from 'classnames/bind';
import { format } from 'date-util';
import { useCallback, useMemo } from 'react';
import styles from './columns.module.scss';

const cx = classnames.bind(styles);

const SORT_DIRECTIONS: ('descend' | 'ascend')[] = ['descend', 'ascend'];
const RESIZABLE_MIN_WIDTH = 68;

const RESIZABLE_COLUMNS: Record<string, number> = {
  office_member: 160,
  title: 200,
  ap_payee_name: 160,
  total_amount: 140,
  report_approval_names: 160,
};

type Column = TableProps<AdminInvoiceReportColumn>['columns'][number];
type ColumnWithResizableMin = Column & { resizableMinWidth?: number };

export type UseColumnsProps = {
  selectedRowKeys: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectRow: (record: AdminInvoiceReportColumn, checked: boolean) => void;
  isAllSelected: boolean;
  isAllDisabled: boolean;
  isLoading?: boolean;
  columnWidths?: Record<string, number>;
  onCancel?: (record: AdminInvoiceReportColumn) => void;
  onDelete?: (record: AdminInvoiceReportColumn) => void;
};

const getColumnWidth = (
  columnKey: string,
  columnWidths: Record<string, number>
): number | undefined => {
  const defaultWidth = RESIZABLE_COLUMNS[columnKey];
  if (defaultWidth === undefined) return undefined;
  return columnWidths[columnKey] ?? defaultWidth;
};

const isResizable = (columnKey: string): boolean =>
  columnKey in RESIZABLE_COLUMNS;

export const getTotalTableWidth = (
  columnWidths: Record<string, number>
): number => {
  const fixedWidths = Object.entries(styles)
    .filter(([key]) => key.startsWith('column_') && key.endsWith('_width'))
    .filter(([key]) => {
      const col = key.replace('column_', '').replace('_width', '');
      return !isResizable(col);
    })
    .reduce((sum, [, val]) => sum + Number(val), 0);

  const resizableWidths = Object.entries(RESIZABLE_COLUMNS).reduce(
    (sum, [key, defaultWidth]) => sum + (columnWidths[key] ?? defaultWidth),
    0
  );

  return fixedWidths + resizableWidths;
};

export const useColumns = ({
  selectedRowKeys,
  onSelectAll,
  onSelectRow,
  isAllSelected,
  isAllDisabled,
  isLoading = false,
  columnWidths = {},
  onCancel,
  onDelete,
}: UseColumnsProps): ColumnWithResizableMin[] => {
  const { t } = useTranslation();
  const container = useGlobalContainerRef();

  const sorterConfig = useMemo(
    () =>
      !isLoading
        ? {
            sorter: true,
            sortDirections: SORT_DIRECTIONS,
            showSorterTooltip: { mouseEnterDelay: 0.8 },
          }
        : { showSorterTooltip: false },
    [isLoading]
  );

  const handleCheckboxChange = useCallback(
    (e: CheckboxChangeEvent, record: AdminInvoiceReportColumn) => {
      e.stopPropagation();
      onSelectRow(record, e.target.checked);
    },
    [onSelectRow]
  );

  const onStopPropagation = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
    },
    []
  );

  const allColumns: ColumnWithResizableMin[] = useMemo(
    () => [
      // 1. Checkbox
      {
        title: (
          <Checkbox
            className={cx(isAllDisabled ? 'disabledCustomCheckbox' : '')}
            indeterminate={selectedRowKeys.length > 0 && !isAllSelected}
            checked={isAllSelected}
            disabled={isAllDisabled || isLoading}
            onChange={(e) => onSelectAll(e.target.checked)}
          />
        ),
        dataIndex: 'select',
        key: 'select',
        fixed: 'left',
        onCell: () => ({ onClick: onStopPropagation }),
        render(_, record) {
          const canSelect = record.delete_action.allowed;
          const tooltipTitle = !canSelect
            ? record.delete_action.reason ?? ''
            : '';
          return (
            <TooltipCell
              title={tooltipTitle}
              wrapperClassName={cx(styles['columnTooltip'])}
            >
              <Checkbox
                checked={selectedRowKeys.includes(record.id)}
                disabled={!canSelect || isLoading}
                className={cx(!canSelect ? 'disabledCustomCheckbox' : '')}
                onChange={(e: CheckboxChangeEvent) =>
                  handleCheckboxChange(e, record)
                }
              />
            </TooltipCell>
          );
        },
        align: 'center',
        width: `${styles['column_select_width']}px`,
        className: cx(styles['checkboxContainer']),
      },
      // 2. Application number
      {
        title: t('admin_invoice_reports.table.columns.application_number'),
        dataIndex: 'number',
        key: 'number',
        fixed: 'left',
        render(value, record) {
          const errorMessages = record.error_messages ?? [];
          return (
            <>
              <div className={cx('numberContainer')}>
                {value}
                <Tooltip
                  title={t('open_preview')}
                  getPopupContainer={container}
                  overlayClassName={cx(styles['columnTooltip'])}
                >
                  <div className={cx('iconWrapper')}>
                    <IconDrawer />
                  </div>
                </Tooltip>
              </div>
              <StatusCell status={record.status} />
              {errorMessages.length > 0 && (
                <div className={cx('errorTagsContainer')}>
                  {errorMessages.map((msg, index) => {
                    const tagStatus: TagProps['status'] =
                      msg.type === 'warn' ? 'warning' : 'error';
                    return (
                      <Tooltip
                        key={`error-tag-${index}`}
                        title={msg.message}
                        getPopupContainer={container}
                        overlayClassName={cx(styles['columnTooltip'])}
                      >
                        <Tag
                          status={tagStatus}
                          closable={false}
                          outline={false}
                          icon
                        >
                          {msg.label}
                        </Tag>
                      </Tooltip>
                    );
                  })}
                </div>
              )}
            </>
          );
        },
        width: `${styles['column_number_width']}px`,
        ...sorterConfig,
      },
      // 3. Creation date
      {
        title: t('admin_invoice_reports.table.columns.created_date'),
        dataIndex: 'created_at',
        key: 'created_at',
        render: (value) => format(value, 'YYYY/MM/DD'),
        width: `${styles['column_created_at_width']}px`,
        ...sorterConfig,
      },
      // 4. Application date
      {
        title: t('admin_invoice_reports.table.columns.application_date'),
        dataIndex: 'submitted_at',
        key: 'submitted_at',
        render: (value) => format(value, 'YYYY/MM/DD'),
        width: `${styles['column_submitted_at_width']}px`,
        ...sorterConfig,
      },
      // 5. Applicant (always shown in admin view)
      {
        title: t('admin_invoice_reports.table.columns.applicant'),
        dataIndex: 'office_member',
        key: 'office_member',
        render(value: { name?: string; number?: string } | undefined) {
          if (!value) return '';
          const tooltipTitle = `${value.name ?? ''} (${value.number ?? ''})`;
          return (
            <TooltipCell
              title={tooltipTitle}
              wrapperClassName={cx(styles['columnTooltip'])}
            >
              <div className={cx(styles['applicantContainer'])}>
                <div className={cx(styles['applicantName'])}>
                  {value.name || ''}
                </div>
                <div className={cx(styles['applicantCode'])}>
                  {value.number || ''}
                </div>
              </div>
            </TooltipCell>
          );
        },
        width:
          getColumnWidth('office_member', columnWidths) ??
          `${styles['column_office_member_width']}px`,
        resizable: isResizable('office_member'),
        resizableMinWidth: RESIZABLE_MIN_WIDTH,
      },
      // 6. Title
      {
        title: t('admin_invoice_reports.table.columns.title'),
        dataIndex: 'title',
        key: 'title',
        render(value) {
          return (
            <TooltipCell
              title={value}
              wrapperClassName={cx(styles['columnTooltip'])}
            >
              {value || ''}
            </TooltipCell>
          );
        },
        width:
          getColumnWidth('title', columnWidths) ??
          `${styles['column_title_width']}px`,
        resizable: isResizable('title'),
        resizableMinWidth: RESIZABLE_MIN_WIDTH,
      },
      // 7. Payee
      {
        title: t('admin_invoice_reports.table.columns.payee'),
        dataIndex: 'ap_payee_name',
        key: 'ap_payee_name',
        render(value) {
          return (
            <TooltipCell
              title={value}
              wrapperClassName={cx(styles['columnTooltip'])}
            >
              {value || ''}
            </TooltipCell>
          );
        },
        width:
          getColumnWidth('ap_payee_name', columnWidths) ??
          `${styles['column_ap_payee_name_width']}px`,
        resizable: isResizable('ap_payee_name'),
        resizableMinWidth: RESIZABLE_MIN_WIDTH,
      },
      // 8. Trade date
      {
        title: t('admin_invoice_reports.table.columns.expense_booking_date'),
        dataIndex: 'book_date',
        key: 'book_date',
        render: (value) => format(value, 'YYYY/MM/DD'),
        width: `${styles['column_book_date_width']}px`,
        ...sorterConfig,
      },
      // 9. Payment due date
      {
        title: t('admin_invoice_reports.table.columns.payment_due_date'),
        dataIndex: 'due_date',
        key: 'due_date',
        render: (value) => format(value, 'YYYY/MM/DD'),
        width: `${styles['column_due_date_width']}px`,
        ...sorterConfig,
      },
      // 10. Total amount
      {
        title: t('admin_invoice_reports.table.columns.total_amount'),
        dataIndex: 'total_amount',
        key: 'total_amount',
        render(value) {
          return <div>{value || ''}</div>;
        },
        width:
          getColumnWidth('total_amount', columnWidths) ??
          `${styles['column_total_amount_width']}px`,
        align: 'right',
        resizable: isResizable('total_amount'),
        resizableMinWidth: RESIZABLE_MIN_WIDTH,
      },
      // 11. Approvers
      {
        title: t('admin_invoice_reports.table.columns.approver'),
        dataIndex: 'report_approval_names',
        key: 'report_approval_names',
        render(value) {
          return (
            <TooltipCell
              title={value}
              wrapperClassName={cx(styles['columnTooltip'])}
            >
              {value || ''}
            </TooltipCell>
          );
        },
        width:
          getColumnWidth('report_approval_names', columnWidths) ??
          `${styles['column_report_approval_names_width']}px`,
        resizable: isResizable('report_approval_names'),
        resizableMinWidth: RESIZABLE_MIN_WIDTH,
      },
      // 12. Approved date
      {
        title: t('admin_invoice_reports.table.columns.approval_date'),
        dataIndex: 'approved_at',
        key: 'approved_at',
        render: (value) => format(value, 'YYYY/MM/DD'),
        width: `${styles['column_approved_at_width']}px`,
        ...sorterConfig,
      },
      // 13. Payment date
      {
        title: t('admin_invoice_reports.table.columns.payment_date'),
        dataIndex: 'payment_date',
        key: 'payment_date',
        render: (value) => format(value, 'YYYY/MM/DD'),
        width: `${styles['column_payment_date_width']}px`,
        ...sorterConfig,
      },
      // 14. Received invoice
      {
        title: t('admin_invoice_reports.table.columns.attached_invoice'),
        dataIndex: 'invoice_file',
        key: 'invoice_file',
        render(value) {
          if (!value) return '';
          return (
            <div className={cx(styles['invoiceFileContainer'])}>
              <TooltipCell
                title={value.name}
                wrapperClassName={cx(styles['columnTooltip'])}
              >
                {value.name}
              </TooltipCell>
            </div>
          );
        },
        width: `${styles['column_invoice_file_width']}px`,
      },
      // 15. Action (3-dots menu + 詳細 link)
      {
        key: 'action',
        title: '',
        render(_, record) {
          return (
            <ActionCell
              record={record}
              onCancel={onCancel}
              onDelete={onDelete}
            />
          );
        },
        width: `${styles['column_action_width']}px`,
        ellipsis: false,
        align: 'center',
        fixed: 'right',
      },
    ],
    [
      selectedRowKeys,
      onSelectAll,
      isAllSelected,
      isAllDisabled,
      isLoading,
      columnWidths,
      onCancel,
      onDelete,
      t,
      container,
      sorterConfig,
      handleCheckboxChange,
      onStopPropagation,
    ]
  );

  return allColumns;
};
